import React, { useEffect, useState, useCallback, useRef } from "react";
import "./attendee-sdk.css";
import ZoomMtgEmbedded, { SuspensionViewType } from "@zoom/meetingsdk/embedded";
import MeetingSdkProps from "../../interfaces/meeting-sdk";
import debounce from "lodash.debounce";
import { Modifier, ModifierArguments } from "@popperjs/core";
import { toast } from "react-toastify";
import VideoPopup from "../../components/video-pop-up/video-pop-up";


const AttenchatCustomStyle: Modifier<"customStyle", {}> = {
  name: "customStyle",
  enabled: true,
  phase: "write",
  fn: ({ state }: ModifierArguments<{}>) => {
    state.elements.popper.classList.add("attendee-chat-style");
  },
};

const chatModifiers = [AttenchatCustomStyle];

const AttendeeMeetingView: React.FC<MeetingSdkProps> = ({
  meetingNumber,
  password,
  userName,
  userEmail,
  zakToken,
  tk,
  signature,
  setLoading,
  meetingSdkViewFlag,
  getHeightOfMeeting,
  getHeightOfMeetingResponsive,
  hideQna
}) => {
  const [isPopupVisible, setPopupVisible] = useState(false);
  const client = ZoomMtgEmbedded.createClient();
  const windowWidthRef = useRef<number>(window.innerWidth);
  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  const attendeeLoginUrl = `${baseUrl}/login/attendee/${meetingNumber}`;
  let widthForVideo: any;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const openPanels = useCallback(
    debounce(async () => {
      // Open Chat panel
      const chatPanel = document.querySelector(
        '[aria-label="Chat Small Meeting6"]'
      ) as HTMLElement;

      if (chatPanel) {
        chatPanel.click();
      } else {
        console.warn("Chat panel not found.");
      }

      // Open QnA panel
      const qnAPanel = document.querySelector(
        '[aria-label="QA Meeting6"]'
      ) as HTMLElement;

      if (!hideQna) {
        if (qnAPanel) {
          qnAPanel.click();
        } else {
          console.warn("QnA panel not found.");
        }
      }
    }, 600),
    []
  );

  useEffect(() => {
    const handleResize = () => { windowWidthRef.current = window.innerWidth };
    window.addEventListener('resize', handleResize);
    handleResize();
    let isMounted = true;
    if (window.innerWidth < 413) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      widthForVideo = window.innerWidth * 0.9;
    } else if (window.innerWidth < 415) {
      widthForVideo = window.innerWidth * 0.9;
    } else if (window.innerWidth < 450) {
      widthForVideo = window.innerWidth * 0.85;
    } else if (window.innerWidth < 992) {
      widthForVideo = window.innerWidth * 0.9;
    } else {
      widthForVideo = 420;
    }
    const startMeeting = async () => {
      const sdkKey = process.env.REACT_APP_SDK_KEY;
      let pixelPerfectVideoWidth = 69; // 100%

      try {
        const meetingSDKElement = document.getElementById("meetingSDKElement");
        const meetingSDKChatElement = document.getElementById(
          "attendeeattndeeChatElement"
        );
        const isBelow992 = windowWidthRef.current < 992;

        if (!meetingSDKElement) {
          console.error("Meeting SDK elements not found.");
          return;
        }

        if (meetingSdkViewFlag === 'custom' && meetingSDKElement && !isBelow992) {
          const pixelValue = (100 / 100) * window.innerWidth; // 100 --> viewport width 
          const customWidth = pixelValue;
          const customWidthMetting = Math.round(customWidth);
          if (windowWidthRef.current < 3100 && windowWidthRef.current > 2304) {
            pixelPerfectVideoWidth = 70 + 15;
          }
          if (windowWidthRef.current < 2305 && windowWidthRef.current > 2047) {
            pixelPerfectVideoWidth = 59 + 14;
          }
          if (windowWidthRef.current < 2046 && windowWidthRef.current > 1919) {
            pixelPerfectVideoWidth = 55 + 17;
          }
          if (windowWidthRef.current < 1918 && windowWidthRef.current > 1706) {
            pixelPerfectVideoWidth = 50 + 20;
          }
          widthForVideo = (customWidthMetting - (284 + 424) - (pixelPerfectVideoWidth)); // 315 right + 40 + 15 space
          meetingSDKElement.setAttribute("style", "left: 450px !important;"); // 424 + 20
        }

        await client.init({
          zoomAppRoot: meetingSDKElement,
          language: "en-US",
          customize: {
            video: {
              defaultViewType: "active" as SuspensionViewType,
              isResizable: true,
              popper: {
                disableDraggable: true,
              },
              viewSizes: {
                default: {
                  width: widthForVideo,
                  height: 0,
                },
                ribbon: {
                  width: 300,
                  height: 250,
                },
              },
            },
            chat: {
              popper: {
                disableDraggable: false,
                anchorElement: meetingSDKChatElement,
                placement: "right",
                modifiers: chatModifiers,
              },
            },
          },
        });

        if (isMounted) {
          await client
            .join({
              signature,
              sdkKey,
              meetingNumber,
              password,
              userName,
              userEmail,
              tk: tk || "",
              zak: zakToken || "",
            })
            .then(() => {
              document.body.classList.add(`attendee-view`);
              openPanels();
              // Listen for meeting end event
              client.on("connection-change", (payload) => {
                if (payload.state === "Closed") {
                  // window.location.href = attendeeLoginUrl;
                  sessionStorage.clear();
                  window.location.href = `/feedback/${meetingNumber}`;
                }
              });
              client.on("peer-share-state-change", (payload) => {
                if (payload) {
                  const { action } = payload;
                  // if (action === 'Start') {
                  //   shareScreenView(action);
                  // }
                  if (action === 'Stop') {
                    shareScreenView(action);
                  }
                } else {
                  console.log('NO SCREEN SHARED ----------------');
                }
              });
              setDefaultGallaryView();
            })
            .catch((error) => {
              if (error.reason === "participant exist") {
                sessionStorage.clear();
                toast.error(
                  "You are already in the meeting on another device or tab.",
                  {
                    onClose: () => {
                      window.location.reload();
                    },
                  }
                );
              } else if (error.reason === "meeting locked") {
                setPopupVisible(true);
                sessionStorage.clear();
              } else {
                setPopupVisible(false);
                sessionStorage.clear();
                toast.error(
                  `Joining meeting: ${error.reason || error.message}`,
                  {
                    onClose: () => {
                      window.location.href = attendeeLoginUrl;
                    },
                  }
                );
              }
            });
        }
      } catch (error) {
        toast.error(`An error occurred while starting the meeting ${error}`);
        sessionStorage.clear();
        setLoading(false);
      }
    };

    startMeeting();
    return () => {
      isMounted = false;
      window.removeEventListener('resize', handleResize);
    };
  }, [
    meetingNumber,
    password,
    userName,
    userEmail,
    signature,
    tk,
    zakToken,
    setLoading,
    meetingSdkViewFlag
  ]);

  const calculateWidth = () => {
    return window.innerWidth;
  };

  const toggleFullScreen = useCallback(() => {
    const elem = document.getElementById("meetingSDKElement");
    if (elem) {
      if (!document.fullscreenElement) {
        if (elem.requestFullscreen) {
          elem.requestFullscreen();
          updateSdkMeetingSize(calculateWidth());
          addBackButtonToHeader();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          updateSdkMeetingSize(420);
        }
      }
    } else {
      console.error("Element not found for full-screen mode.");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to add a back button to the Zoom SDK header
function addBackButtonToHeader() {
  // Check if the button already exists
  const backBtn = document.getElementById('backButton')
  if (backBtn) {
    backBtn.style.display = 'block';
    return;
  }

  // Locate the Zoom SDK header container
  const headerContainer = document.querySelector('.css-1mctt5q'); // Adjust the class to match the actual header element you find
  if (!headerContainer) {
    console.error('Zoom SDK header not found!');
    return;
  }

  // Create the back button
  const backButton = document.createElement('button');
  backButton.id = 'backButton';
  backButton.innerText = 'Back';
  backButton.style.position = 'absolute';
  backButton.style.right = '10px';
  backButton.style.padding = '10px';
  backButton.style.backgroundColor = 'rgb(36 36 36)';
  backButton.style.border = 'none';
  backButton.style.cursor = 'pointer';
  backButton.style.zIndex = '1000';  // Ensure it's above other elements
  backButton.style.display = 'block';
  backButton.style.color = 'white';

  // Append the button to the header
  headerContainer.appendChild(backButton);

  // Add event listener for the button
  backButton.addEventListener('click', () => {
    // You can use window.history.back() or navigate to a specific URL
    document.exitFullscreen();
    updateSdkMeetingSize(widthForVideo);
    backButton.style.display = 'none';
  });
}

// Call the function to add the back button once the Zoom SDK is initialized

  const handleFullScreenChange = useCallback(() => {
    if (!document.fullscreenElement) {
      if (meetingSdkViewFlag === 'custom') {
        updateSdkMeetingSize(widthForVideo);
      }
      else {
        updateSdkMeetingSize(widthForVideo);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
  }, [handleFullScreenChange]);

  const updateSdkMeetingSize = (widthRef: number) => {
    // const client = ZoomMtgEmbedded.createClient();
    client.updateVideoOptions({
      defaultViewType: "active" as SuspensionViewType,
      viewSizes: {
        default: {
          width: widthRef,
          height: 0,
        },
      },
    });
  };

  const setDefaultGallaryView = () => {
    setTimeout(() => {
      const parentElement = document.querySelector(
        '.attendee-view div[aria-label="view mode tabs"]'
      );
      const qnaElement = document.querySelector('button[aria-label="Q&A"]') as HTMLElement;

      if (qnaElement && hideQna) {
        qnaElement.style.display = "none";
      }

      const elementChat = document.querySelector('[aria-label="Chat"].zoom-MuiPopper-root.css-0') as HTMLElement;
      // if (elementQnA && meetingSdkViewFlag === 'custom') {
      if (meetingSdkViewFlag === 'custom') {
        elementChat?.classList?.remove('css-0');
        elementChat?.classList?.add('css-0-1');
        const meetingSDKElement = document.getElementById("meetingSDKElement") as HTMLElement;
        const childElement = meetingSDKElement.querySelector(
          '.zoom-MuiPaper-root.zoom-MuiPaper-elevation.zoom-MuiPaper-rounded.zoom-MuiPaper-elevation1.css-1636b17'
        ) as HTMLElement;
        if (childElement) {
          const height = childElement.clientHeight;
          getHeightOfMeeting(height);
        } else {
          console.log('Child element with the specified classes not found.');
        }
      }

      if (elementChat && meetingSdkViewFlag === 'default') {
        elementChat?.classList?.add('css-0');
      }

      if (parentElement) {
        // Get all button elements within the parent
        const buttons = parentElement?.querySelectorAll("button");

        // Loop through each button element
        buttons?.forEach((button) => {
          // Check if the button's aria-label is not 'thumbnail-gallery'
          // Hide the button
          button.style.display = "none";
        });
      } else {
        console.error(
          "The parent element with the specified aria-label was not found."
        );
      }
      // Responsive height calc
      const isBelow992 = windowWidthRef.current < 992;
      if (isBelow992) {
        const fullscreenbtn = document.querySelector('.fullscreen-button')  as HTMLElement;;
        fullscreenbtn.innerHTML = 'Fullscreen';
        const meetingSDKElement = document.getElementById("meetingSDKElement") as HTMLElement;
        const childElement = meetingSDKElement.querySelector(
          '.zoom-MuiPaper-root.zoom-MuiPaper-elevation.zoom-MuiPaper-rounded.zoom-MuiPaper-elevation1.css-1636b17'
        ) as HTMLElement;
        if (childElement) {
          const height = childElement.clientHeight;
          getHeightOfMeetingResponsive(height);
        }
      }

      setLoading(false);
    }, 1500);
  };

  const shareScreenView = (mode: string) => {
    if (mode === 'Stop') {
      if(meetingSdkViewFlag === 'custom'){
      updateSdkMeetingSize(900);
      }
    }
  }
  
  

  return (
    <div className="meeting-sdk-container">
      <div id="meetingSDKElement" className="meeting-sdk-element"></div>
      <button onClick={toggleFullScreen} className="fullscreen-button">
       Speaker Video Full Screen
      </button>
      <VideoPopup
        visible={isPopupVisible}
        onClose={() => setPopupVisible(false)}
        attendeeLoginUrl={attendeeLoginUrl}
        eventId={meetingNumber}
        userEmail={userEmail}
      />
    </div>
  );
};

export default AttendeeMeetingView;
